import React from 'react';

import {observer} from "mobx-react";
import {BaseProps} from "../../../infrastructure/models/props/base-props";
import {Loader} from "../../../core/components/loading/loader";
import {AD_DISPLAYS, COMPONENT_STATES, FETCHING_STATES} from "../../../core/consts";
import {EntityScoresWidgetEmptyState} from "./entity-scores-widget.empty-state";
import {EntityScoresWidgetPlaceholder} from "./placeholders/entity-scores-widget.placeholder";
import {List} from "../../../core/components/misc/list/list";
import {InfiniteScroll, InfiniteScrollComponentProps} from "../../../core/components/infinite-scroller/infinite-scroll";
import {EntityScoresWidgetGroups} from "./groups/entity-scores-widget.groups";
import {ENTITY_SCORES} from "../entity-scores-widget.consts";
import {EntityScoresWidgetNavigationButtons} from "./navigation-buttons/entity-scores-widget.navigation-buttons";
import {EntityScoresWidgetSection} from "./section/entity-scores-widget.section";
import {EntityScoresWidgetLiveGroups} from "./groups/entity-scores-widget.live-groups";
import {TagSnippetWidgetAsync} from "../../tag-snippet-widget/components/tag-snippet-widget.async";
import {APP} from "../../../core/app/consts";
import styles from "../styles/entity-scores-empty-state.module.scss";
import classNames from "classnames";
import {ExternalAd} from "../../../core/components/ad/external-ad/external-ad";

export class EntityScoresWidgetContentProps extends BaseProps {
}

export const EntityScoresWidgetContent = observer(({
                                                     controller,
                                                     onFilterChange,
                                                     context,
                                                     isSideBar
                                                   }: EntityScoresWidgetContentProps) => {
  const {state} = controller;
  const {
    sections,
    fetchingState,
    componentState,
    shouldScrollInfiniteLists,
    shouldRenderTitle,
    shouldRenderLoader,
    gamesByDateAndRound,
    hasMore,
    shouldRenderGenerateTagIndex,
    shouldRenderAd,
    device: {isSmallView}
  } = state;

  const emptyStateProps = {
    controller,
    onFilterChange
  }

  const loaderProps = {
    when: fetchingState === FETCHING_STATES.fetching || componentState === COMPONENT_STATES.loading,
    Component: () => <EntityScoresWidgetPlaceholder context={context}/>
  }

  const sectionsProps = {
    items: sections,
    ListItem: EntityScoresWidgetSection,
    itemPropName: 'section',
    listItemProps: {
      controller,
      onFilterChange,
      isSideBar
    },
  }

  const scrollProps: InfiniteScrollComponentProps = {
    loadMore: async () => shouldRenderLoader && await controller.action(ENTITY_SCORES.ACTION_HANDLERS.fetchNextPage),
    Loader: (props) => shouldRenderLoader && <EntityScoresWidgetPlaceholder {...props} count={2} gamesCount={3}/>,
    intersectionObserverMargin: ENTITY_SCORES.INTERSECTION_MARGIN,
    hasMore
  }

  const gamesListProps = {
    items: gamesByDateAndRound,
    shouldRenderTitle,
    controller,
    isSideBar
  };

  const tagSnippetProps = {
    widgetType: 'entityScores',
    source: APP.ANALYTICS.SOURCES.scoresList,
    isSideBar: !isSmallView
  }

  const adProps = {
    countryId: state.appState.locale?.userCountryId,
    isSmallView,
    langId: state.appState.locale.language.id,
    theme: state.appState.theme,
    config: state.adConfig.AD336X280,
    isMock: state.appState.settings.config.adDisplay === AD_DISPLAYS.placeholders,
    className: classNames(styles.ad_container),
    controller
  }

  return (
      <Loader {...loaderProps}>
        <EntityScoresWidgetEmptyState {...emptyStateProps}/>
        <EntityScoresWidgetLiveGroups controller={controller} isSideBar={isSideBar}/>
        {
          shouldScrollInfiniteLists ? <InfiniteScroll {...scrollProps}> {/* only on competitions pages*/}
            <EntityScoresWidgetGroups {...gamesListProps}/>
          </InfiniteScroll> : <List {...sectionsProps}/>
        }
        <EntityScoresWidgetNavigationButtons controller={controller} onFilterChange={onFilterChange}/>
        {shouldRenderGenerateTagIndex ? <TagSnippetWidgetAsync {...tagSnippetProps}/> : null}
        {shouldRenderAd ? <ExternalAd {...adProps}/> : null}
      </Loader>
  )
});
